import { createPortal } from 'react-dom'
import { useNotificationStore, type Notification } from '../store/notificationStore'

// Defensive coercion for the title/message slots — callers occasionally
// pass an Error object or a TanStack mutation error blob. Rendering an
// object directly would crash the whole shell with React #31.
function coerceToText(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (value instanceof Error) return value.message
  try { return JSON.stringify(value) } catch { return String(value) }
}

const iconMap = {
  success: 'check_circle',
  error: 'error',
  warning: 'warning',
  info: 'info',
} as const

const colorMap = {
  success: 'text-teal-600',
  error: 'text-error',
  warning: 'text-amber-600',
  info: 'text-primary',
} as const

function Toast({ n, onDismiss }: { n: Notification; onDismiss: () => void }) {
  return (
    <div
      role="alert"
      className="liquid-glass rounded-2xl flex items-start gap-3 px-4 py-3 min-w-72 max-w-96 animate-in slide-in-from-right-4 duration-200"
    >
      <span
        className={`material-symbols-outlined flex-shrink-0 mt-0.5 ${colorMap[n.type]}`}
        aria-hidden="true"
      >
        {iconMap[n.type]}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-on-surface">{coerceToText(n.title)}</p>
        {n.message != null && (() => {
          const txt = coerceToText(n.message)
          return txt ? <p className="text-xs text-on-surface-variant mt-0.5">{txt}</p> : null
        })()}
      </div>
      <button
        onClick={onDismiss}
        aria-label="Benachrichtigung schließen"
        className="flex-shrink-0 text-on-surface-variant hover:text-on-surface transition-colors"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 16 }} aria-hidden="true">
          close
        </span>
      </button>
    </div>
  )
}

export default function ToastContainer() {
  const { notifications, dismiss } = useNotificationStore()

  return createPortal(
    // aria-live="assertive" for errors, "polite" for the rest — we use a single
    // polite region and rely on role="alert" per toast for errors.
    // Portaled to <body> at z-[9999] so confirmations always sit above modals
    // (z-[70]) and full-screen overlays (CommandPalette / tour at z-[200]).
    <div
      aria-live="polite"
      aria-atomic="false"
      className="fixed bottom-[calc(1.5rem+var(--qm-bar,0px))] right-6 z-[9999] flex flex-col gap-2 items-end"
    >
      {notifications.map((n) => (
        <Toast key={n.id} n={n} onDismiss={() => dismiss(n.id)} />
      ))}
    </div>,
    document.body,
  )
}
